/* Piece Verse cart drawer: Order Protection toggle, "You might also like", empty-state sync.
   Works with Dawn's <cart-drawer> (getSectionsToRender / getSectionInnerHTML) and Section Rendering API.
   All prices, discounts and totals are rendered by Liquid; this file only calls the Cart API and swaps sections. */
(function () {
  if (window.bkCartLoaded) return;
  window.bkCartLoaded = true;

  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const RECS_SECTION = 'bk-cart-recommendations';
  const GENERIC_ERROR = 'Something went wrong. Please try again.';

  const getDrawer = () => document.querySelector('cart-drawer');
  const getInner = () => document.querySelector('cart-drawer .drawer__inner');

  function setLoading(on) {
    const drawer = getDrawer();
    const inner = getInner();
    if (inner) inner.classList.toggle('is-bk-loading', on);
    if (drawer) drawer.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* POST to a Cart API endpoint (add / change) and ask for the drawer sections in the same round trip. */
  async function cartRequest(endpoint, payload) {
    const drawer = getDrawer();
    const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
    const body = { ...payload };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const url = `${(window.routes && window.routes[`cart_${endpoint}_url`]) || `/cart/${endpoint}`}`;
    const response = await fetch(url.endsWith('.js') ? url : `${url}.js`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || (typeof result.errors === 'string' ? result.errors : '') || GENERIC_ERROR);
    }
    return result;
  }

  /* Swap the drawer sections without opening the drawer (the drawer is already open for user actions). */
  function applySections(state) {
    const drawer = getDrawer();
    if (!drawer || !state || !state.sections) return;
    drawer.getSectionsToRender().forEach((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = state.sections[section.id];
      if (target && html) target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
    });
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  }

  function refocus(selector) {
    const drawer = getDrawer();
    const el = drawer && drawer.querySelector(selector);
    if (!el) return;
    if (typeof trapFocus === 'function') trapFocus(drawer, el);
    else el.focus();
  }

  /* Keeps cart-drawer.is-empty in sync after any re-render; removes a cart that only holds the protection line. */
  class BkCartState extends HTMLElement {
    connectedCallback() {
      const drawer = this.closest('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', this.dataset.empty === 'true');
      if (this.dataset.onlyProtection === 'true' && !BkCartState.busy) this.removeLoneProtection();
    }

    async removeLoneProtection() {
      const line = Number(this.dataset.protectionLine);
      if (!line) return;
      BkCartState.busy = true;
      try {
        applySections(await cartRequest('change', { line, quantity: 0 }));
      } catch (error) {
        console.warn('bk-cart: could not remove lone protection line', error);
      } finally {
        BkCartState.busy = false;
      }
    }
  }

  class BkCartExtras extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    showError(selector, message) {
      const box = this.querySelector(selector);
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    errorText(error) {
      return (error && error.message) || this.dataset.errGeneric || GENERIC_ERROR;
    }

    /* Protection: checked -> add.js qty 1, unchecked -> change.js qty 0. Never pre-checked by the theme. */
    onChange = async (event) => {
      const input = event.target.closest('[data-bk-protect-input]');
      if (!input) return;
      event.stopPropagation(); // keep Dawn's quantity handler on <cart-drawer-items> out of this
      this.showError('[data-bk-protect-error]', '');
      const variantId = Number(input.dataset.variantId);
      const wantOn = input.checked;
      input.disabled = true;
      setLoading(true);
      try {
        const state = wantOn
          ? await cartRequest('add', { id: variantId, quantity: 1 })
          : await cartRequest('change', { id: variantId, quantity: 0 });
        applySections(state);
        refocus('#BkCartProtection');
      } catch (error) {
        input.checked = !wantOn;
        this.showError('[data-bk-protect-error]', this.errorText(error));
      } finally {
        input.disabled = false;
        setLoading(false);
      }
    };

    /* Recommendation "Add" for single-variant products. */
    onClick = async (event) => {
      const button = event.target.closest('[data-bk-recs-add]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      this.showError('[data-bk-recs-error]', '');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      setLoading(true);
      try {
        applySections(await cartRequest('add', { id: Number(button.dataset.variantId), quantity: 1 }));
        refocus('.cart-item__name');
      } catch (error) {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
        this.showError('[data-bk-recs-error]', this.errorText(error));
      } finally {
        setLoading(false);
      }
    };

    /* "You might also like": related products of the first cart item, rendered by sections/bk-cart-recommendations.liquid. */
    async loadRecommendations() {
      const productId = this.dataset.firstProductId;
      const endpoint = this.dataset.recommendationsUrl;
      const slot = this.querySelector('[data-bk-recs]');
      if (!productId || !endpoint || !slot) return;
      try {
        const query = new URLSearchParams({ product_id: productId, limit: '8', intent: 'related', section_id: RECS_SECTION });
        const response = await fetch(`${endpoint}?${query}`);
        if (!response.ok) throw new Error(`recommendations ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const list = doc.querySelector('[data-bk-recs-ready]');
        if (!list || !this.isConnected) return; // none to show, or the drawer re-rendered meanwhile
        this.querySelector('[data-bk-recs-list]').replaceChildren(list);
        slot.hidden = false;
      } catch (error) {
        // Recommendations are optional: keep the section hidden and leave the cart usable.
        console.warn('bk-cart: recommendations unavailable', error);
      }
    }
  }

  customElements.define('bk-cart-state', BkCartState);
  customElements.define('bk-cart-extras', BkCartExtras);
})();
